/* eslint-disable no-nested-ternary */
import { useCurrentAccount } from '@application/api';
import {
  PreloadController,
  SeoController,
  SubscribeToDogBreedButtonController,
} from '@application/components';
import { RedirectWithStatus } from '@application/components/app/App/RedirectWithStatus';
import type { PageState } from '@application/types';
import { useRouter, useTranslatedUrl } from '@application/utils';
import { DOG_BREED_INTEREST_BANNER_ID } from '@presentation/components';
import { BreedPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/Breed/components/PageSkeleton';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormatBreeders } from '../BreederListPageController/api/useFormatBreeders';
import { useFormatLitters } from '../LitterListPageController/api/useFormatLitters';
import { useFormatBreed } from './api/useFormatBreed';
import { useGetBreed } from './api/useGetBreed';
import { useGetBreeders } from './api/useGetBreeders';
import { useGetLitters } from './api/useGetLitters';

export const BreedPageController: FC = () => {
  const { infoFromCurrentPath, language, routeLocationDescriptor, getLocaleLinksForTranslations } =
    useRouter();

  const { currentAccount } = useCurrentAccount();
  const { breed, breedLoading, breedError } = useGetBreed(infoFromCurrentPath.params[0]);
  const { formattedBreed, imagesToPreload } = useFormatBreed(breed);
  const { litters, littersLoading } = useGetLitters(breed?.name ? [breed.name] : undefined);
  const { breeders, breedersLoading } = useGetBreeders(breed?.name ? [breed.name] : undefined);

  const { formattedBreeders } = useFormatBreeders(breeders);
  const { formattedLitters } = useFormatLitters(litters);

  const pageState = useMemo((): PageState => {
    if (breedLoading) return 'loading';
    if (breedError) return 'error';
    if (!formattedBreed?.title) return 'empty';
    return 'data';
  }, [breedError, breedLoading, formattedBreed?.title]);

  const translation = useMemo(() => {
    if (!breed?.translations.length) return;
    return breed?.translations?.find((x) => x.language === language) ?? undefined;
  }, [breed?.translations, language]);

  useTranslatedUrl(breed?.translations);

  const breedersLink = useMemo(() => {
    if (!formattedBreeders || formattedBreeders?.length < 1) return;
    return routeLocationDescriptor('breederList', {
      params: [breed?.baseBreedName ?? breed?.name ?? ''],
    });
  }, [breed?.baseBreedName, breed?.name, formattedBreeders, routeLocationDescriptor]);

  const littersLink = useMemo(() => {
    if (!formattedLitters || formattedLitters?.length < 1) return;
    return routeLocationDescriptor('litterList', {
      params: [breed?.baseBreedName ?? breed?.name ?? ''],
    });
  }, [breed?.baseBreedName, breed?.name, formattedLitters, routeLocationDescriptor]);

  const showLittersCta = useMemo(() => {
    if (!littersLoading && !formattedLitters) return false;
    return true;
  }, [formattedLitters, littersLoading]);

  const showBreedersCta = useMemo(() => {
    if (!breedersLoading && !formattedBreeders) return false;
    return true;
  }, [breedersLoading, formattedBreeders]);

  const noIndex = useMemo(() => {
    return !breedLoading && !formattedBreed.description;
  }, [breedLoading, formattedBreed.description]);

  return (
    <>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={translation?.title ?? undefined}
        description={translation?.seoDescription ?? undefined}
        keywords={translation?.seoKeywords ?? undefined}
        thumbnail={formattedBreed?.images?.profile}
        localeLinks={getLocaleLinksForTranslations(breed?.translations)}
        noIndex={noIndex}
      />
      {pageState === 'loading' && <PageSkeleton />}
      {(pageState === 'empty' || pageState === 'error') && (
        <RedirectWithStatus status={404} to="/404" />
      )}
      {pageState === 'data' && (
        <BreedPage
          {...formattedBreed}
          name={breed?.name}
          moreLittersLink={littersLink}
          moreBreedersLink={breedersLink}
          litterItems={formattedLitters}
          breederItems={formattedBreeders}
          litterItemsLoading={littersLoading}
          breederItemsLoading={breedersLoading}
          subscribeBtn={
            <SubscribeToDogBreedButtonController
              subscriptionEmail={currentAccount?.email ?? null}
              breedName={breed?.name}
              iconOnlyMobile
              onFallbackClick={() => {
                document
                  .getElementById(DOG_BREED_INTEREST_BANNER_ID)
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
              hasADog={currentAccount?.hasADog}
            />
          }
          showLittersCta={showLittersCta}
          showBreedersCta={showBreedersCta}
        />
      )}
    </>
  );
};
